// Styles
import './PricesRequestForm.scss';

// Libs
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// Common
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
// Components
import SelectField from '@/common/components/molecules/SelectField/SelectField';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { FILE_TYPES } from '@/common/constants';
// Contexts
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import { useTagManager } from '@/common/contexts/TagManagerContext';
// Hooks
import postPriceFile from '@/ebat/api/price-file/postPriceFile';
// Constants
import { TAG_MANAGER } from '@/ebat/constants';

import {
  PricesRequestFormDefaultProps,
  PricesRequestFormPropTypes,
} from './PricesRequestForm.proptypes';

const PricesRequestForm = ({ user, isLoading, disabled }) => {
  const { t } = useTranslation();
  const { zergRush } = useAuth();
  const { onPushEvent } = useTagManager();
  const { modalIsOpen, onCloseModal } = useModalContext();

  const [isAlreadyInProgress, setIsAlreadyInProgress] = useState(false);

  const {
    mutate: mutatePostPriceFile,
    isLoading: isLoadingPostPriceFile,
  } = useMutation(postPriceFile);

  const isDisabled = () => {
    const loading = isLoading || isLoadingPostPriceFile;

    return {
      loading,
      disabled: loading || zergRush || disabled,
    };
  };

  const handleIsDisabled = (format) =>
    isDisabled.disabled || !format || format === '' || isAlreadyInProgress;

  /**
   * Push dataLayer
   * @type {function(): void}
   * @param format {string}
   */
  const handlePushDataLayer = useCallback(
    (format) =>
      onPushEvent({
        event: TAG_MANAGER.EVENTS.CTA_PRICE_REQUEST,
        format,
      }),
    [onPushEvent],
  );

  return (
    <Formik
      initialValues={{
        format: '',
        email: user.email,
      }}
      onSubmit={async (values) => {
        mutatePostPriceFile(
          { ...values },
          {
            onSuccess: () => {
              const { format } = values;

              if (modalIsOpen) {
                onCloseModal();
              }
              handlePushDataLayer(format);

              toast.success(
                <Snack message={t('common:messages:forms.success.default')} />,
              );
            },
            onError: (e) => {
              if (e.response.status === 429) {
                setIsAlreadyInProgress(true);
              } else {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              }
            },
          },
        );
      }}
    >
      {({ values }) => (
        <Form className="price-request" data-qa-id="prices-request-form">
          <Field
            as={SelectField}
            context="format-field"
            required
            id="format"
            name="format"
            options={FILE_TYPES}
            placeholder={t('ebat:entities:account.informations.select_format')}
            disabled={isDisabled.disabled}
          >
            {t('common:messages:forms.fields.format')}
          </Field>
          <div className="submit-container">
            <ButtonLoading
              className="submit-button"
              context="price-request-save"
              disabled={handleIsDisabled(values.format)}
              isLoading={isDisabled.loading}
            >
              {t('common:messages:actions.request')}
            </ButtonLoading>
            {isAlreadyInProgress && (
              <div className="submit-error">
                {t(
                  'ebat:entities:layout.header.my_account.price_information_message',
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

PricesRequestForm.defaultProps = {
  ...PricesRequestFormDefaultProps,
};

PricesRequestForm.propTypes = {
  ...PricesRequestFormPropTypes,
};

export default PricesRequestForm;
